import React, { useMemo } from "react";
import { geoMercator, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import topo from "./assets/italy_regions.topo.json";
import { tokens } from "./theme";
import { regionName } from "./regionNames";

// openpolis reg_istat_code_num → ITTER107-style codes used in the warehouse
const NUM_TO_ITTER = {
  1: "ITC1", 2: "ITC2", 3: "ITC4", 4: "ITDA", 5: "ITD3", 6: "ITD4",
  7: "ITC3", 8: "ITD5", 9: "ITE1", 10: "ITE2", 11: "ITE3", 12: "ITE4",
  13: "ITF1", 14: "ITF2", 15: "ITF3", 16: "ITF4", 17: "ITF5", 18: "ITF6",
  19: "ITG1", 20: "ITG2",
};

const W = 430, H = 520;
const fc = feature(topo, topo.objects.regions);
const projection = geoMercator().fitExtent([[8, 8], [W - 8, H - 8]], fc);
const pathGen = geoPath(projection);
const PATHS = fc.features.map((f) => ({
  code: NUM_TO_ITTER[f.properties.reg_istat_code_num],
  d: pathGen(f),
}));

const lerp = (a, b, t) => Math.round(a + (b - a) * t);
const rgb = (c1, c2, t) =>
  `rgb(${lerp(c1[0], c2[0], t)},${lerp(c1[1], c2[1], t)},${lerp(c1[2], c2[2], t)})`;

function colorScale(dm, diverging) {
  const lo = dm ? [18, 52, 59] : [224, 242, 244];
  const hi = dm ? [51, 198, 212] : [0, 67, 75];
  const neg = dm ? [248, 113, 113] : [185, 28, 28];
  const pos = dm ? [52, 211, 153] : [4, 120, 87];
  const mid = dm ? [45, 49, 57] : [243, 244, 241];
  if (!diverging) return (t) => rgb(lo, hi, t);
  return (t) => (t < 0.5 ? rgb(neg, mid, t * 2) : rgb(mid, pos, (t - 0.5) * 2));
}

/** Custom-SVG choropleth of the 20 Italian regions (d3-geo + vendored
 *  TopoJSON — M0 decision: no react-simple-maps on React 19).
 *  Keyboard-accessible: every region is a focusable button. */
export default function ItalyMap({ values, format, diverging, dm, lang,
  selected, hovered, onSelect, onHover, noValueHint, srCaption }) {
  const T = tokens(dm);
  const scale = useMemo(() => colorScale(dm, diverging), [dm, diverging]);

  const domain = useMemo(() => {
    const vals = Object.values(values || {}).filter((v) => v !== null && v !== undefined);
    if (!vals.length) return null;
    if (diverging) {
      const m = Math.max(Math.abs(Math.min(...vals)), Math.abs(Math.max(...vals))) || 1;
      return { min: -m, max: m };
    }
    return { min: Math.min(...vals), max: Math.max(...vals) };
  }, [values, diverging]);

  const fillFor = (code) => {
    const v = values?.[code];
    if (v === null || v === undefined || !domain) return dm ? "#1F2229" : "#F3F4F1";
    const span = domain.max - domain.min || 1;
    return scale((v - domain.min) / span);
  };

  return (
    <div data-testid="italy-map">
      <svg viewBox={`0 0 ${W} ${H}`} role="group" aria-label={srCaption}
        style={{ width: "100%", height: "auto", display: "block" }}>
        {PATHS.map(({ code, d }) => {
          const v = values?.[code];
          const name = regionName(code, lang);
          const label = `${name}: ${v === null || v === undefined
            ? (noValueHint || "—") : format(v)}`;
          const active = selected === code || hovered === code;
          return (
            <path key={code} d={d} data-testid={`italy-map-region-${code}`}
              role="button" tabIndex={0} aria-label={label} aria-pressed={selected === code}
              onClick={() => onSelect && onSelect(code)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect && onSelect(code); }
              }}
              onMouseEnter={() => onHover && onHover(code)}
              onMouseLeave={() => onHover && onHover(null)}
              onFocus={() => onHover && onHover(code)}
              onBlur={() => onHover && onHover(null)}
              style={{
                fill: fillFor(code), cursor: "pointer", outline: "none",
                stroke: active ? (dm ? "#F3F4F6" : "#111827") : T.border,
                strokeWidth: active ? 1.75 : 0.6,
                transition: "fill 200ms, stroke 120ms",
              }}>
              <title>{label}</title>
            </path>
          );
        })}
      </svg>
      {domain && (
        <div className="flex items-center gap-2 mt-2" aria-hidden="true">
          <span className="text-[10px] pulse-num" style={{ color: T.muted }}>{format(domain.min)}</span>
          <div style={{
            flex: 1, height: 8, borderRadius: 2,
            background: `linear-gradient(90deg, ${scale(0)}, ${scale(0.5)}, ${scale(1)})`,
          }} />
          <span className="text-[10px] pulse-num" style={{ color: T.muted }}>{format(domain.max)}</span>
        </div>
      )}
    </div>
  );
}
